import { ApiError, type RequestInfo } from "@circle-react/config/CustomErrors";

import { t } from "@/i18n-js/instance";

import {
  buildBackendRequestHeaders,
  isSameOrigin,
  omitHeaders,
} from "./backendRequestHeaders";
import { safeJsonParse } from "./safeJsonParse";

export interface ErrorJsonBody {
  error_details?: string | Record<string, unknown>;
  failure?: {
    message: string;
  };
  message?: string;
  view_only_masquerading?: boolean;
}

export interface JsonFetchOptions extends RequestInit {
  headers?: Record<string, string>;
  observeResponseHeaders?: readonly {
    name: string;
    onValue(value: string | null): void;
  }[];
  redactRequestHeaders?: readonly string[];
  sameOriginHeaders?: readonly string[];
}

const notifyObservedHeaders = ({
  observeResponseHeaders,
  response,
  url,
}: {
  observeResponseHeaders: JsonFetchOptions["observeResponseHeaders"];
  response: Response;
  url: string;
}): void => {
  if (!isSameOrigin(url) || !observeResponseHeaders) return;

  for (const observer of observeResponseHeaders) {
    observer.onValue(response.headers.get(observer.name));
  }
};

export async function jsonFetchRequest<ResponseData>(
  url: string,
  options?: JsonFetchOptions,
) {
  const {
    observeResponseHeaders,
    redactRequestHeaders,
    sameOriginHeaders,
    ...requestOptions
  } = options ?? {};
  const method = requestOptions.method || "GET";
  const headers = buildBackendRequestHeaders({
    headers: requestOptions.headers,
    method,
    sameOriginHeaders,
    url,
  });

  const requestInfo = {
    url,
    method,
    headers: omitHeaders(headers, redactRequestHeaders),
    body: requestOptions.body,
  };

  const response = await fetch(url, {
    ...requestOptions,
    headers,
    credentials: "same-origin",
  });

  if (response.ok) {
    notifyObservedHeaders({ observeResponseHeaders, response, url });

    try {
      const text = await response.text();
      if (text.length === 0) return undefined as ResponseData;
      return JSON.parse(text) as ResponseData;
    } catch (error: unknown) {
      const details = error instanceof Error ? error.message : "";
      throw new ApiError({
        message: `Failed to parse response`,
        status: response.status,
        statusText: response.statusText,
        errorDetails: details,
        request: requestInfo,
      });
    }
  } else {
    return await throwApiError(response, requestInfo);
  }
}

const throwApiError = async (response: Response, request?: RequestInfo) => {
  let json: ErrorJsonBody;

  try {
    const text = await response.text();
    json = text.length > 0 ? (JSON.parse(text) as ErrorJsonBody) : {};
  } catch {
    json = { message: `Failed to parse error response` };
  }

  const { status, statusText } = response;

  if (json?.view_only_masquerading) {
    throw new ApiError({
      message: t("view_only_error"),
      status,
      statusText,
      errorDetails: t("view_only_error"),
      body: json,
      request,
      response,
    });
  }

  const message =
    json?.message || json?.failure?.message || "Failed to retrieve data!";

  const { error_details = json?.message } = json;

  throw new ApiError({
    message,
    status,
    statusText,
    errorDetails: error_details,
    body: json,
    request,
    response,
  });
};

export const reactQueryGet = <ResponseData = any>(
  url: string,
  options?: Pick<
    JsonFetchOptions,
    | "headers"
    | "redirect"
    | "redactRequestHeaders"
    | "sameOriginHeaders"
    | "signal"
  >,
) => jsonFetchRequest<ResponseData>(url, { method: "GET", ...options });

export const reactQueryPut = <ResponseData, BodyParameters extends object>(
  url: string,
  bodyParams?: BodyParameters,
) =>
  jsonFetchRequest<ResponseData>(url, {
    method: "PUT",
    body: JSON.stringify(bodyParams),
  });

export const reactQueryPatch = <ResponseData, BodyParameters extends object>(
  url: string,
  bodyParams?: BodyParameters,
  options?: Pick<
    JsonFetchOptions,
    | "headers"
    | "redirect"
    | "redactRequestHeaders"
    | "sameOriginHeaders"
    | "signal"
  >,
) =>
  jsonFetchRequest<ResponseData>(url, {
    method: "PATCH",
    body: JSON.stringify(bodyParams),
    ...options,
  });

export const reactQueryPost = <
  ResponseData = any,
  BodyParameters extends object = object,
>(
  url: string,
  bodyParams?: BodyParameters,
  options?: Pick<
    JsonFetchOptions,
    | "headers"
    | "observeResponseHeaders"
    | "redirect"
    | "redactRequestHeaders"
    | "sameOriginHeaders"
    | "signal"
  >,
) =>
  jsonFetchRequest<ResponseData>(url, {
    method: "POST",
    body: JSON.stringify(bodyParams),
    ...options,
  });

export const reactQueryDelete = <ResponseData, BodyParameters extends object>(
  url: string,
  bodyParams?: BodyParameters,
) => {
  const options: JsonFetchOptions = {
    method: "DELETE",
  };

  if (bodyParams) {
    options.body = JSON.stringify(bodyParams);
  }

  return jsonFetchRequest<ResponseData>(url, options);
};

/**
 * @deprecated Please use jsonFetchRequest instead
 */
export interface FetchOptions extends Partial<RequestInit> {
  forReactQuery?: boolean;
  headers?: Record<string, string>;
  sameOriginHeaders?: readonly string[];
}

/**
 * @deprecated Please use jsonFetchRequest instead
 */
export type OptionsType = FetchOptions | undefined;

/**
 * @deprecated Please use jsonFetchRequest instead
 */
export const serializeJsonResponse = async <ResponseData>(
  response: Response,
): Promise<ResponseData | undefined | Record<string, never>> => {
  if (response.ok) {
    const text = await response.text();
    if (text.length > 0) {
      const parsed = safeJsonParse<ResponseData>(text);
      return parsed || {};
    }
    return {};
  }

  return throwApiError(response);
};

/**
 * @deprecated Please use jsonFetchRequest from backendRequestHelpers.ts instead, or write your own fetch() call
 */
export async function fetchRequest<ResponseData>(
  url: string,
  options: OptionsType = {},
): Promise<Response | ResponseData | Record<string, never> | undefined> {
  const {
    headers = {},
    forReactQuery: isForReactQuery,
    sameOriginHeaders,
    ...passThroughOptions
  } = options;

  const mergedHeaders = buildBackendRequestHeaders({
    headers,
    method: passThroughOptions.method || "GET",
    sameOriginHeaders,
    url,
  });

  const response = await fetch(url, {
    ...passThroughOptions,
    headers: mergedHeaders,
    credentials: "same-origin",
  });

  if (!isForReactQuery) {
    return response;
  }

  return serializeJsonResponse<ResponseData>(response);
}
